import { useState } from "react";
import type { ProductVideo } from "../content/types";

/**
 * Click-to-play video, the pattern every enterprise security vendor uses for
 * narrated film: a poster frame carries the section until someone asks for the
 * video. The `<video>` element is only mounted on click, so a visitor who
 * never presses play downloads the poster and nothing else — the file itself
 * is several megabytes and most visitors will not watch it.
 *
 * Autoplay is deliberately absent. This is a minute of narration, not an
 * ambient loop, and nothing on this site should start talking unprompted.
 */
export function VideoBlock({ video }: { video: ProductVideo }) {
  const [playing, setPlaying] = useState(false);

  return (
    <figure className="pdp__video">
      {playing ? (
        <video
          className="pdp__video-player"
          src={video.src}
          poster={video.poster}
          controls
          autoPlay
          playsInline
          preload="auto"
        >
          <track
            kind="captions"
            src={video.captions}
            srcLang="en"
            label="English"
            default
          />
        </video>
      ) : (
        <button
          type="button"
          className="pdp__video-poster"
          onClick={() => setPlaying(true)}
          aria-label={`Play video: ${video.title}`}
        >
          <img src={video.poster} alt="" width={1920} height={1080} />
          <span className="pdp__video-play" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor">
              <path d="M8 5v14l11-7z" />
            </svg>
          </span>
          <span className="pdp__video-meta">
            <b>{video.title}</b>
            <small>{video.duration}</small>
          </span>
        </button>
      )}
      <figcaption>{video.caption}</figcaption>
    </figure>
  );
}
